import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AddressParts, availableZipCodes, IOlavEntry, ISolrDocList, ISolrGroup, ISolrOlavKeyAddress, ISolrResponse } from '@mwe/models';
import { distinctUntilChanged, filter, map, startWith, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { addAdditionalPlaceholders } from '../../../../ui/components/src/lib/address-search/utils/utils';

@Injectable()
export class AddressSearchLogic {
  private readonly SEARCH_URL = '/api/solr/olav/select';
  private readonly IDENT_URL = '/api/solr/olav/select/ident';
  private readonly OLAV_KEY_SEARCH = '/api/solr/olav/searchOlavKey';
  private readonly SPECIAL_CHARS_REGEX = /[ ()]/g;

  private ignoreAvailableZipCodes = false;

  private http = inject(HttpClient);
  private translationService = inject(TranslateService);

  search({
    value,
    isIdentSearch = false,
    ignoreAvailableZipCodes = false,
  }: {
    value: Partial<AddressParts>;
    isIdentSearch?: boolean;
    ignoreAvailableZipCodes?: boolean;
  }) {
    const url = isIdentSearch ? this.IDENT_URL : this.SEARCH_URL;
    this.ignoreAvailableZipCodes = ignoreAvailableZipCodes;
    return of(value).pipe(
      // Early validation to prevent unnecessary HTTP calls
      filter(addressParts => this.isValidAddressParts(addressParts)),

      // Transform to HTTP params
      map(addressParts => this.generateAddressParams(addressParts)),

      // Make HTTP request with error handling
      switchMap(params => this.http.get<ISolrResponse>(url, { params })),

      // Process the entire response chain in one transformation
      map(response => this.processCompleteResponse(response, value)),

      // Provide fallback for empty results
      startWith([] as ISolrDocList[]),
    );
  }

  searchPredefinedOlavKey(key: string, processType = '') {
    return of(key).pipe(
      distinctUntilChanged(),
      map(key => new HttpParams().set('olavKey', key).set('process', processType === 'superschnell-bestellen' ? 'OI' : '')),
      switchMap(params => this.http.get<{ response: ISolrOlavKeyAddress }>(this.OLAV_KEY_SEARCH, { params })),
      map(({ response }) => {
        if (response?.numFound >= 1 && this.isAnAvailableZipCode(response?.docs?.[0]?.plz)) {
          return response;
        }
        return null;
      }),
    );
  }

  private isValidAddressParts(value: Partial<AddressParts>) {
    return Boolean(value && Object.values(value).some(val => val && val.trim().length > 0));
  }

  private processCompleteResponse(response: ISolrResponse, addressParts?: Partial<AddressParts>): ISolrDocList[] {
    // Early return for invalid response
    if (!response?.grouped) {
      return [this.getNoResultsMessage()];
    }

    const groups = this.getFirstAvailableGroup(response.grouped);
    if (!groups?.groups?.length) {
      return [this.getNoResultsMessage()];
    }

    // Process all transformations in a single reduce operation
    const processedSuggestions = groups.groups.reduce((acc: ISolrDocList[], group) => {
      const processedGroup = this.processGroup(group);
      if (!processedGroup || this.shouldFilterOutGroup(processedGroup)) {
        return acc;
      }

      // Clean duplicated keys inline
      const cleanedGroup = this.cleanDuplicatedAnlageKeys(processedGroup);

      if (!cleanedGroup.docs.length) {
        return acc;
      }

      // Handle door splitting inline if needed
      if (addressParts?.door && cleanedGroup.docs.length > 1) {
        acc.push(...this.splitDoorSuggestion(cleanedGroup));
      } else {
        acc.push(cleanedGroup);
      }

      return acc;
    }, []);

    // Return sorted results or no results message
    return processedSuggestions.length > 0 ? this.sortSuggestions(processedSuggestions) : [this.getNoResultsMessage()];
  }

  private shouldFilterOutGroup(suggestion: ISolrDocList) {
    const firstDoc = suggestion.docs[0];
    return this.isIncompleteOlavKey(firstDoc.olavKey, suggestion.identNumFound);
  }

  private splitDoorSuggestion(suggestion: ISolrDocList) {
    const [firstDoc, ...restDocs] = suggestion.docs;
    const splitSuggestions: ISolrDocList[] = [];

    // Process first document
    const updatedFirstDoc = { ...firstDoc };
    this.updateTuerNr(updatedFirstDoc);
    splitSuggestions.push({
      ...suggestion,
      docs: [updatedFirstDoc],
    });

    // Process remaining documents with unique door tracking
    const uniqueTuerNr = new Set([updatedFirstDoc.tuerNr]);

    for (const doc of restDocs) {
      const updatedDoc = { ...doc };
      this.updateTuerNr(updatedDoc);

      if (!uniqueTuerNr.has(updatedDoc.anladr1)) {
        uniqueTuerNr.add(updatedDoc.anladr1);
        splitSuggestions.push({
          ...suggestion,
          docs: [updatedDoc],
        });
      }
    }

    return splitSuggestions;
  }

  private sortSuggestions(suggestions: ISolrDocList[]) {
    return suggestions.toSorted((a, b) => {
      const aDoc = a?.docs?.[0];
      const bDoc = b?.docs?.[0];

      if (!aDoc || !bDoc) return 0;
      return this.compareFields(aDoc, bDoc, ['plz', 'gemeinde', 'strasse', 'gebadr1', 'tuerNr']);
    });
  }

  private getNoResultsMessage() {
    return [this.translationService.instant('address-input.hasNoResults.title')] as unknown as ISolrDocList;
  }

  private generateAddressParams(value: Partial<AddressParts>) {
    return Object.entries(value)
      .reduce((params, [key, val]) => {
        if (!val?.length) return params;
        const paramValue =
          key === 'zipCode' ? addAdditionalPlaceholders(this.replaceSpecialChars(val, key)) : this.replaceSpecialChars(val, key);

        return params.append(key, paramValue);
      }, new HttpParams())
      .set('enableZipCodeFiltering', this.ignoreAvailableZipCodes);
  }

  private replaceSpecialChars(value: string, key?: string) {
    const escapeSpecialChars = (value: string): string => {
      const regex = key === 'building' ? /[-\\^$+?.|[\]{}]/g : /[-/\\^$+?.|[\]{}]/g;
      return value.replace(regex, '\\$&');
    };
    return escapeSpecialChars(value.replace(this.SPECIAL_CHARS_REGEX, '*'));
  }

  private getFirstAvailableGroup(grouped: ISolrResponse['grouped']) {
    return grouped.strasse || grouped.gebadr1 || grouped.tuerNr || grouped.anladr1 || grouped.plzGemeindeKombi;
  }

  private processGroup(group: ISolrGroup['groups'][number]) {
    if (!group?.doclist?.docs?.length) return null;

    const firstDoc = group.doclist.docs[0];
    if (!this.isAnAvailableZipCode(firstDoc?.plz)) return null;

    return { ...group.doclist, groupValue: group.groupValue } as ISolrDocList;
  }

  private updateTuerNr(doc: IOlavEntry) {
    if (doc.tuerNr !== doc.anladr1) {
      if (doc.anladr1?.trim().startsWith(doc.gebadr1)) {
        const newTuerNr = doc.anladr1.trim().replace(doc.gebadr1, '').trim().replace('/', '');
        doc.tuerNr = newTuerNr.replace('+', ' ');
      } else {
        doc.tuerNr = doc.anladr1?.trim();
      }
    }
  }

  private cleanDuplicatedAnlageKeys(suggestion: ISolrDocList) {
    const seenAnlageKeys = new Set<string>();

    return {
      ...suggestion,
      docs: suggestion.docs.filter(doc => {
        if (seenAnlageKeys.has(doc.anlageKey)) {
          return false;
        }
        seenAnlageKeys.add(doc.anlageKey);
        return true;
      }),
    } as ISolrDocList;
  }

  private isIncompleteOlavKey(olavKey: string, identMatches: number) {
    return identMatches <= 1 && !olavKey.length;
  }

  private isAnAvailableZipCode(plz: string) {
    if (this.ignoreAvailableZipCodes) return true;
    return availableZipCodes.includes(plz);
  }

  private compareFields(aDoc: IOlavEntry, bDoc: IOlavEntry, fields: string[]): number {
    for (const field of fields) {
      const aValue = (aDoc[field] || '') as string;
      const bValue = (bDoc[field] || '') as string;

      if (aValue !== bValue) {
        return aValue.localeCompare(bValue, 'de', { numeric: true, sensitivity: 'accent' });
      }
    }
    return 0;
  }
}
